import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { PmsSourceKey } from '@/types/pms.types';
import { comingSoonVendors, type ComingSoonVendor } from '../landing/comingSoon';
import { getVendorDefinition, vendorRegistry } from '../vendor-settings/vendorRegistry';
import type { VendorDefinition } from '../vendor-settings/types';
import type { ConnectedStatus } from './heroModel';

/**
 * Card grids from the `Integrations Redesign` prototype: auto-fit columns
 * with a 300 px floor for the first-run set-up cards and a 240 px floor for
 * the compact directory tiles.
 */
export const SETUP_GRID = 'grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))]';
export const DIRECTORY_GRID = 'grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))]';

/** Shared card surface: 12 px radius, hairline border, no shadow (prototype). */
export const LANDING_CARD = 'rounded-xl shadow-none';

/**
 * Which design the tiles follow: the customer landing's (`Integrations
 * Redesign` prototype) or the internal console's (DV-3500 Integrations tab
 * design), where a tile that can't be chosen is greyed out and says why in
 * its body.
 */
export type DirectoryVariant = 'landing' | 'console';

/** The console's greyed-out tile: muted surface, faded wordmark. */
const UNAVAILABLE_CARD = 'bg-muted/50 [&_img]:opacity-50';

/**
 * Vendor wordmark, sized per vendor (the prototype tunes each height for
 * optical balance; the fallback is 24 px). Fixed 28 px row so cards line up.
 */
export const VendorLogo = ({ name, logo, logoHeightPx }: { name: string; logo: string; logoHeightPx?: number }) => (
  <img
    src={logo}
    alt={name}
    style={{ height: logoHeightPx ?? 24 }}
    className="w-auto max-w-[150px] object-contain"
  />
);

const LockRow = ({ text }: { text: string }) => (
  <div className="mt-auto flex items-center gap-2 text-muted-foreground">
    <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    <Typography variant="body-sm" className="text-muted-foreground">{text}</Typography>
  </div>
);

/**
 * A vendor the account can't connect while another is. The landing puts the
 * reason in the lock row; the console says it in the body and labels the lock
 * row (`lockedLabel`).
 */
const LockedVendorTile = ({ vendor, note, lockedLabel }: { vendor: VendorDefinition; note: string; lockedLabel?: string }) => (
  <Card className={cn(LANDING_CARD, 'flex min-h-29 flex-col gap-4 px-5 py-4.5', lockedLabel && UNAVAILABLE_CARD)}>
    <div className="flex h-7 items-center">
      <VendorLogo name={vendor.displayName} logo={vendor.logo} logoHeightPx={vendor.logoHeightPx} />
    </div>
    {lockedLabel ? (
      <>
        <Typography variant="body-sm" className="text-muted-foreground">{note}</Typography>
        <LockRow text={lockedLabel} />
      </>
    ) : (
      <LockRow text={note} />
    )}
  </Card>
);

/** No connection (DV-3500): the vendor and how it connects, nothing to click. */
const VendorInfoTile = ({ vendor, note }: { vendor: VendorDefinition; note: string }) => (
  <Card className={`${LANDING_CARD} flex min-h-29 flex-col gap-4 px-5 py-4.5`}>
    <div className="flex h-7 items-center">
      <VendorLogo name={vendor.displayName} logo={vendor.logo} logoHeightPx={vendor.logoHeightPx} />
    </div>
    <Typography variant="body-sm" className="text-muted-foreground">{note}</Typography>
  </Card>
);

interface ComingSoonTileProps {
  vendor: ComingSoonVendor;
  label: string;
  footer?: ReactNode;
  variant?: DirectoryVariant;
}

/**
 * A coming-soon vendor. `footer` is the page's own call to action (the
 * customer's contact link); none by default. On the landing the label is a
 * badge, which wraps under the wordmark when the tile is too narrow for both
 * (near the grid's 240 px floor), rather than overflowing the card or
 * shrinking the logo. On the console the tile is greyed out and the label is
 * its body text.
 */
export const ComingSoonTile = ({ vendor, label, footer, variant = 'landing' }: ComingSoonTileProps) =>
  variant === 'console' ? (
    <Card className={cn(LANDING_CARD, 'flex min-h-29 flex-col gap-4 px-5 py-4.5', UNAVAILABLE_CARD)}>
      <div className="flex h-7 items-center">
        <VendorLogo name={vendor.name} logo={vendor.logo} logoHeightPx={vendor.logoHeightPx} />
      </div>
      <Typography variant="body-sm" className="text-muted-foreground">{label}</Typography>
      {footer}
    </Card>
  ) : (
    <Card className={`${LANDING_CARD} flex min-h-29 flex-col gap-4 px-5 py-4.5`}>
      <div className="flex min-h-7 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <VendorLogo name={vendor.name} logo={vendor.logo} logoHeightPx={vendor.logoHeightPx} />
        <Badge variant="secondary" className="shrink-0 whitespace-nowrap uppercase tracking-[0.04em]">
          {label}
        </Badge>
      </div>
      {footer}
    </Card>
  );

/** The directory's words; each page passes its own voice (DV-3500). */
export interface DirectoryCopy {
  heading: string;
  /** Under the heading. */
  note?: string;
  /** Under the tiles. */
  footnote?: string;
  lockedWhileConnected: (connected: string) => string;
  lockedWhileDisabled: (connected: string) => string;
  comingSoon: string;
}

/** The console's words: the lock row's label, and its own line on how each vendor connects. */
export interface ConsoleDirectoryCopy extends DirectoryCopy {
  locked: string;
  connectNotes: Record<PmsSourceKey, string>;
}

type VariantProps =
  | { variant?: 'landing'; copy: DirectoryCopy }
  | { variant: 'console'; copy: ConsoleDirectoryCopy };

export type VendorDirectoryProps = VariantProps & {
  /** The account's connection, or null for none: every registry vendor then shows how it connects. */
  connected: ConnectedStatus | null;
  /** Rendered at the foot of each coming-soon tile. */
  comingSoonFooter?: ReactNode;
  /** An extra first tile. */
  leading?: ReactNode;
  /** The section heading's level: h2 on the landing, h3 under the console's tab heading. */
  headingAs?: 'h2' | 'h3';
};

/**
 * "More integrations": the vendors the account is NOT connected to, locked
 * (one PMS per organization), plus the coming-soon vendors (spec §4.5). With
 * no connection (DV-3500) every vendor shows how it connects instead. No card
 * here has a button; links come from the page (`leading`, `comingSoonFooter`).
 * Props only — no routes or session reads (DV-3500 §4.1).
 *
 * The connected vendor is resolved via `getVendorDefinition` (not a raw
 * `vendorRegistry[key]` index) because `connected.pmsSource` is server data,
 * not guaranteed to be a known registry key. When it resolves to nothing, the
 * lock note falls back to the raw source string and every registry vendor is
 * shown locked (none of them is provably the connected one).
 */
export const VendorDirectory = (props: VendorDirectoryProps) => {
  const { connected, copy, comingSoonFooter, leading, headingAs = 'h2' } = props;
  const variant = props.variant ?? 'landing';
  const consoleCopy = props.variant === 'console' ? props.copy : null;
  const tiles = connected === null ? (
    Object.values(vendorRegistry).map((vendor) => (
      <VendorInfoTile key={vendor.key} vendor={vendor} note={consoleCopy?.connectNotes[vendor.key] ?? vendor.connectNote} />
    ))
  ) : (
    <LockedVendors connected={connected} copy={copy} lockedLabel={consoleCopy?.locked} />
  );
  return (
    <section aria-labelledby="more-integrations" className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Typography as={headingAs} id="more-integrations" variant="heading-md">{copy.heading}</Typography>
        {copy.note && <Typography as="p" variant="body-sm" className="text-muted-foreground">{copy.note}</Typography>}
      </div>
      <div className={DIRECTORY_GRID}>
        {leading}
        {tiles}
        {comingSoonVendors.map((vendor) => (
          <ComingSoonTile key={vendor.name} vendor={vendor} label={copy.comingSoon} footer={comingSoonFooter} variant={variant} />
        ))}
      </div>
      {copy.footnote && <Typography as="p" variant="body-sm" className="text-muted-foreground">{copy.footnote}</Typography>}
    </section>
  );
};

const LockedVendors = ({ connected, copy, lockedLabel }: { connected: ConnectedStatus; copy: DirectoryCopy; lockedLabel?: string }) => {
  const connectedVendor = getVendorDefinition(connected.pmsSource);
  const connectedDisplayName = connectedVendor?.displayName ?? connected.pmsSource;
  const others = Object.values(vendorRegistry).filter((vendor) => vendor.key !== connectedVendor?.key);
  const note =
    connected.kind === 'disabled'
      ? copy.lockedWhileDisabled(connectedDisplayName)
      : copy.lockedWhileConnected(connectedDisplayName);
  return <>{others.map((vendor) => <LockedVendorTile key={vendor.key} vendor={vendor} note={note} lockedLabel={lockedLabel} />)}</>;
};
